import * as THREE from 'three';
import type { TierDef } from './tiers';

/** Expressive face decals as canvas textures — unique per face style */

const faceTextureCache = new Map<string, THREE.CanvasTexture>();

function makeCanvas(size = 256): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  return c;
}

function drawFace(ctx: CanvasRenderingContext2D, size: number, style: TierDef['face']): void {
  ctx.clearRect(0, 0, size, size);
  const cx = size / 2;
  const cy = size / 2 + size * 0.02;

  // Transparent background — only face features
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const eyeColor = '#2a1840';
  const blush = 'rgba(255, 140, 170, 0.45)';

  const drawEyes = (spread: number, eyeY: number, r: number, sparkle = true) => {
    // Whites
    ctx.fillStyle = '#fffef8';
    ctx.beginPath();
    ctx.ellipse(cx - spread, eyeY, r * 1.15, r * 1.25, 0, 0, Math.PI * 2);
    ctx.ellipse(cx + spread, eyeY, r * 1.15, r * 1.25, 0, 0, Math.PI * 2);
    ctx.fill();
    // Pupils
    ctx.fillStyle = eyeColor;
    ctx.beginPath();
    ctx.ellipse(cx - spread + r * 0.1, eyeY + r * 0.05, r * 0.62, r * 0.7, 0, 0, Math.PI * 2);
    ctx.ellipse(cx + spread + r * 0.1, eyeY + r * 0.05, r * 0.62, r * 0.7, 0, 0, Math.PI * 2);
    ctx.fill();
    if (sparkle) {
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(cx - spread - r * 0.25, eyeY - r * 0.3, r * 0.28, 0, Math.PI * 2);
      ctx.arc(cx + spread - r * 0.25, eyeY - r * 0.3, r * 0.28, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx - spread + r * 0.2, eyeY + r * 0.25, r * 0.12, 0, Math.PI * 2);
      ctx.arc(cx + spread + r * 0.2, eyeY + r * 0.25, r * 0.12, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  const drawBlush = (spread: number, y: number) => {
    ctx.fillStyle = blush;
    ctx.beginPath();
    ctx.ellipse(cx - spread * 1.45, y, size * 0.07, size * 0.045, 0, 0, Math.PI * 2);
    ctx.ellipse(cx + spread * 1.45, y, size * 0.07, size * 0.045, 0, 0, Math.PI * 2);
    ctx.fill();
  };

  const drawSmile = (y: number, width: number, open = false) => {
    ctx.strokeStyle = eyeColor;
    ctx.lineWidth = size * 0.035;
    ctx.beginPath();
    if (open) {
      ctx.fillStyle = '#2a1840';
      ctx.ellipse(cx, y, width, width * 0.55, 0, 0, Math.PI);
      ctx.fill();
      ctx.fillStyle = '#ff8fab';
      ctx.beginPath();
      ctx.ellipse(cx, y + width * 0.15, width * 0.55, width * 0.28, 0, 0, Math.PI);
      ctx.fill();
    } else {
      ctx.arc(cx, y - width * 0.15, width, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.stroke();
    }
  };

  switch (style) {
    case 'spark': {
      drawEyes(size * 0.14, cy - size * 0.02, size * 0.07);
      // Tiny star mouth
      ctx.fillStyle = eyeColor;
      ctx.beginPath();
      ctx.arc(cx, cy + size * 0.14, size * 0.04, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'wisp': {
      drawEyes(size * 0.16, cy - size * 0.04, size * 0.08);
      drawBlush(size * 0.16, cy + size * 0.06);
      drawSmile(cy + size * 0.12, size * 0.1, false);
      break;
    }
    case 'puff': {
      // Happy closed eyes
      ctx.strokeStyle = eyeColor;
      ctx.lineWidth = size * 0.04;
      ctx.beginPath();
      ctx.arc(cx - size * 0.15, cy - size * 0.02, size * 0.08, 1.1 * Math.PI, 1.9 * Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx + size * 0.15, cy - size * 0.02, size * 0.08, 1.1 * Math.PI, 1.9 * Math.PI);
      ctx.stroke();
      drawBlush(size * 0.18, cy + size * 0.08);
      drawSmile(cy + size * 0.14, size * 0.12, true);
      break;
    }
    case 'orb': {
      drawEyes(size * 0.15, cy - size * 0.05, size * 0.075);
      // Content smile
      drawSmile(cy + size * 0.12, size * 0.11);
      // Brow
      ctx.strokeStyle = eyeColor;
      ctx.lineWidth = size * 0.025;
      ctx.beginPath();
      ctx.moveTo(cx - size * 0.22, cy - size * 0.14);
      ctx.quadraticCurveTo(cx - size * 0.15, cy - size * 0.18, cx - size * 0.08, cy - size * 0.14);
      ctx.moveTo(cx + size * 0.08, cy - size * 0.14);
      ctx.quadraticCurveTo(cx + size * 0.15, cy - size * 0.18, cx + size * 0.22, cy - size * 0.14);
      ctx.stroke();
      break;
    }
    case 'moon': {
      // Sleepy crescent eyes
      ctx.strokeStyle = eyeColor;
      ctx.lineWidth = size * 0.04;
      ctx.beginPath();
      ctx.arc(cx - size * 0.14, cy, size * 0.09, 0.2 * Math.PI, 0.9 * Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx + size * 0.14, cy, size * 0.09, 0.2 * Math.PI, 0.9 * Math.PI);
      ctx.stroke();
      drawSmile(cy + size * 0.14, size * 0.09);
      break;
    }
    case 'macaron': {
      drawEyes(size * 0.17, cy - size * 0.06, size * 0.07);
      drawBlush(size * 0.17, cy + size * 0.05);
      // Cute cat mouth
      ctx.strokeStyle = eyeColor;
      ctx.lineWidth = size * 0.03;
      ctx.beginPath();
      ctx.moveTo(cx, cy + size * 0.1);
      ctx.quadraticCurveTo(cx - size * 0.06, cy + size * 0.18, cx - size * 0.1, cy + size * 0.12);
      ctx.moveTo(cx, cy + size * 0.1);
      ctx.quadraticCurveTo(cx + size * 0.06, cy + size * 0.18, cx + size * 0.1, cy + size * 0.12);
      ctx.stroke();
      break;
    }
    case 'planet': {
      drawEyes(size * 0.16, cy - size * 0.08, size * 0.085);
      drawSmile(cy + size * 0.1, size * 0.14, true);
      break;
    }
    case 'star': {
      drawEyes(size * 0.15, cy - size * 0.04, size * 0.08);
      // Big grin
      drawSmile(cy + size * 0.12, size * 0.15, true);
      // Eyebrow arcs
      ctx.strokeStyle = eyeColor;
      ctx.lineWidth = size * 0.03;
      ctx.beginPath();
      ctx.arc(cx - size * 0.15, cy - size * 0.16, size * 0.08, 1.2 * Math.PI, 1.8 * Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx + size * 0.15, cy - size * 0.16, size * 0.08, 1.2 * Math.PI, 1.8 * Math.PI);
      ctx.stroke();
      break;
    }
    case 'comet': {
      // Winky
      drawEyes(size * 0.15, cy - size * 0.04, size * 0.07);
      // Left wink
      ctx.fillStyle = 'rgba(255,248,255,0.95)';
      ctx.fillRect(cx - size * 0.28, cy - size * 0.12, size * 0.2, size * 0.16);
      ctx.strokeStyle = eyeColor;
      ctx.lineWidth = size * 0.04;
      ctx.beginPath();
      ctx.arc(cx - size * 0.15, cy - size * 0.02, size * 0.08, 1.15 * Math.PI, 1.85 * Math.PI);
      ctx.stroke();
      drawSmile(cy + size * 0.12, size * 0.12);
      break;
    }
    case 'truffle': {
      // Mysterious half-lidded
      drawEyes(size * 0.14, cy - size * 0.02, size * 0.07, true);
      ctx.fillStyle = 'rgba(42,24,64,0.35)';
      ctx.fillRect(cx - size * 0.28, cy - size * 0.12, size * 0.5, size * 0.06);
      // Smirk
      ctx.strokeStyle = eyeColor;
      ctx.lineWidth = size * 0.032;
      ctx.beginPath();
      ctx.moveTo(cx - size * 0.08, cy + size * 0.14);
      ctx.quadraticCurveTo(cx + size * 0.05, cy + size * 0.2, cx + size * 0.14, cy + size * 0.1);
      ctx.stroke();
      break;
    }
    case 'cosmic': {
      // Majestic big eyes
      drawEyes(size * 0.18, cy - size * 0.06, size * 0.095);
      drawBlush(size * 0.2, cy + size * 0.08);
      drawSmile(cy + size * 0.16, size * 0.16, true);
      // Crown sparkles
      ctx.fillStyle = '#ffe29a';
      for (let i = 0; i < 5; i++) {
        const a = -0.6 + (i / 4) * 1.2;
        const sx = cx + Math.sin(a) * size * 0.32;
        const sy = cy - size * 0.32 + Math.cos(a) * size * 0.05;
        ctx.beginPath();
        ctx.arc(sx, sy, size * 0.025, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
  }
}

export function getFaceTexture(tier: TierDef): THREE.CanvasTexture {
  const key = tier.face;
  let tex = faceTextureCache.get(key);
  if (tex) return tex;

  const canvas = makeCanvas(256);
  const ctx = canvas.getContext('2d')!;
  drawFace(ctx, 256, tier.face);
  tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  faceTextureCache.set(key, tex);
  return tex;
}

export function createFacePlane(tier: TierDef, radius: number): THREE.Mesh {
  const tex = getFaceTexture(tier);
  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const scale = radius * 1.15;
  const geo = new THREE.PlaneGeometry(scale, scale);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.z = radius * 0.72;
  mesh.position.y = radius * 0.05;
  mesh.name = 'face';
  return mesh;
}
